import { useCallback, useEffect, useState } from 'react';
import { Check, X, Wallet } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchOperationsCoursier,
  validerOperationCoursier,
  refuserOperationCoursier,
  fetchFinances,
  nomTiers,
  TYPES_OPERATION,
  MODES_PAIEMENT,
} from '../lib/dataProvider';
import { attribuerNumero } from '../lib/souches';
import { useAuth } from '../lib/auth';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { PrintDoc } from '../components/print/PrintDoc';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';

const COULEURS = {
  'En attente': 'bg-amber-50 text-amber-800',
  Validée: 'bg-emerald-50 text-emerald-800',
  Refusée: 'bg-red-50 text-red-700',
};

export default function ValidationsCoursier() {
  const { user } = useAuth();
  const [ops, setOps] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [filtre, setFiltre] = useState('En attente');
  const [reglage, setReglage] = useState({});
  const [refus, setRefus] = useState(null);
  const [motif, setMotif] = useState('');
  const [recu, setRecu] = useState(null);

  const load = useCallback(async () => {
    setOps(await fetchOperationsCoursier());
    const fin = await fetchFinances();
    setComptes(fin.soldes);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const valider = async (op) => {
    const cfg = reglage[op.id] ?? {};
    const { error } = await validerOperationCoursier(op.id, {
      utilisateur: user?.name,
      compteId: cfg.compteId ?? op.compteId ?? 'crs',
      mode: cfg.mode ?? MODES_PAIEMENT[0],
    });
    if (error) return toast.error(error);
    const numero = attribuerNumero('recu_paiement', op.id);
    toast.success(`Opération validée et imputée — reçu ${numero}`);
    setRecu({
      ...op,
      numero,
      compteNom: comptes.find((c) => c.id === (cfg.compteId ?? op.compteId ?? 'crs'))?.nom ?? '—',
      mode: cfg.mode ?? MODES_PAIEMENT[0],
    });
    load();
  };

  const refuser = async () => {
    const { error } = await refuserOperationCoursier(refus.id, { motif, utilisateur: user?.name });
    if (error) return toast.error(error);
    toast.success('Opération refusée — le coursier verra le motif');
    setRefus(null);
    setMotif('');
    load();
  };

  const visibles = ops.filter((o) => filtre === 'Toutes' || o.statut === filtre);
  const enAttente = ops.filter((o) => o.statut === 'En attente');

  const beneficiaire = (o) =>
    o.typeOperation === 'tiers'
      ? `${nomTiers(o.tiersId)} · ${o.sensTiers}`
      : o.typeOperation === 'personnel'
        ? `${nomTiers(o.employeId)} · ${o.motifPaie}`
        : (o.categorie ?? '—');

  return (
    <div data-testid="validations-page" className="space-y-6">
      <PageTitle
        emoji="✅"
        titre="Validations des opérations coursier"
        sous="Contrôle du gérant avant toute imputation en caisse, sur un compte tiers ou sur un salaire"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatBlock testid="stat-v-attente" titre="À valider" accent="border-l-amber-500" items={[['Opérations', fmt(enAttente.length)], ['Montant (TND)', fmt(enAttente.reduce((s, o) => s + o.montant, 0), 3)]]} />
        <StatBlock testid="stat-v-validees" titre="Validées" accent="border-l-emerald-600" items={[['Opérations', fmt(ops.filter((o) => o.statut === 'Validée').length)]]} />
        <StatBlock testid="stat-v-refusees" titre="Refusées" accent="border-l-red-600" items={[['Opérations', fmt(ops.filter((o) => o.statut === 'Refusée').length)]]} />
      </div>

      <div className="flex flex-wrap gap-px bg-slate-200">
        {['En attente', 'Validée', 'Refusée', 'Toutes'].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`filtre-${t.toLowerCase().replace(' ', '-')}`}
            onClick={() => setFiltre(t)}
            className={`px-4 py-2.5 text-sm font-bold transition-colors duration-150 ${
              filtre === t ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <section className="border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-validations">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Réf.', 'Date', 'Coursier', 'Type', 'Bénéficiaire / catégorie', 'Motif', 'Reçu', 'Montant (TND)', 'Décaissement', 'Statut', 'Actions'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibles.length === 0 ? (
                <tr data-testid="validations-empty">
                  <td colSpan={11} className="px-3 py-12 text-center text-sm text-slate-500">Aucune opération dans ce filtre.</td>
                </tr>
              ) : (
                visibles.map((o) => {
                  const cfg = reglage[o.id] ?? {};
                  return (
                    <tr key={o.id} data-testid={`validation-row-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{o.id}</td>
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{o.date}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{o.saisiPar}</td>
                      <td className="px-3 py-2 text-sm text-slate-800">{TYPES_OPERATION.find((t) => t.id === o.typeOperation)?.label}</td>
                      <td className="px-3 py-2 text-sm text-slate-700">{beneficiaire(o)}</td>
                      <td className="px-3 py-2 text-sm text-slate-600">{o.libelle}</td>
                      <td className="px-3 py-2">
                        {o.justificatif ? (
                          <img src={o.justificatif} alt="Reçu" className="h-8 w-8 border border-slate-200 object-cover" />
                        ) : (
                          <span className="font-data text-xs text-amber-700">manquant</span>
                        )}
                      </td>
                      <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(o.montant, 3)}</td>
                      <td className="px-3 py-2">
                        {o.statut === 'En attente' ? (
                          <div className="flex gap-1.5">
                            <select
                              data-testid={`compte-${o.id}`}
                              value={cfg.compteId ?? o.compteId ?? 'crs'}
                              onChange={(e) => setReglage((r) => ({ ...r, [o.id]: { ...cfg, compteId: e.target.value } }))}
                              className={`${inputCls} sm:w-40`}
                            >
                              {comptes.map((c) => (
                                <option key={c.id} value={c.id}>{c.nom}</option>
                              ))}
                            </select>
                            <select
                              data-testid={`mode-${o.id}`}
                              value={cfg.mode ?? MODES_PAIEMENT[0]}
                              onChange={(e) => setReglage((r) => ({ ...r, [o.id]: { ...cfg, mode: e.target.value } }))}
                              className={`${inputCls} sm:w-32`}
                            >
                              {MODES_PAIEMENT.map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <span className="font-data text-xs text-slate-500">{o.refImputation ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span data-testid={`v-statut-${o.id}`} className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${COULEURS[o.statut]}`}>
                          {o.statut}
                        </span>
                        {o.motifRefus && <p className="mt-1 text-[11px] text-red-700">{o.motifRefus}</p>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {o.statut === 'En attente' && (
                          <div className="flex justify-end gap-1.5">
                            <button
                              type="button"
                              data-testid={`valider-${o.id}`}
                              onClick={() => valider(o)}
                              className="inline-flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                            >
                              <Check size={13} /> Valider
                            </button>
                            <button
                              type="button"
                              data-testid={`refuser-${o.id}`}
                              onClick={() => setRefus(o)}
                              className="inline-flex items-center gap-1.5 border border-red-300 px-2 py-1 text-xs font-semibold text-red-700 transition-colors duration-150 hover:bg-red-600 hover:text-white"
                            >
                              <X size={13} /> Refuser
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {refus && (
        <div data-testid="refus-overlay" className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-md border border-slate-200 bg-white p-5">
            <h2 className="font-heading text-lg font-black tracking-tight text-slate-900">Refuser {refus.id}</h2>
            <p className="mt-1 text-sm text-slate-600">
              {fmt(refus.montant, 3)} TND — {refus.libelle}. Le motif sera visible par le coursier.
            </p>
            <input
              data-testid="refus-motif"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="ex. reçu illisible, montant non conforme…"
              className="mt-4 h-11 w-full border border-slate-300 bg-slate-50 px-3 text-sm outline-none focus:border-slate-900 focus:bg-white"
            />
            <div className="mt-4 flex gap-3">
              <button type="button" data-testid="refus-confirmer" onClick={refuser} className="h-11 flex-1 bg-red-600 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-red-700">
                Confirmer le refus
              </button>
              <button
                type="button"
                data-testid="refus-annuler"
                onClick={() => {
                  setRefus(null);
                  setMotif('');
                }}
                className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {recu && (
        <PrintDoc
          type="Reçu de paiement / avance de caisse"
          numero={recu.numero}
          date={recu.date}
          tiers={beneficiaire(recu)}
          lignes={[
            ['Nature de l’opération', TYPES_OPERATION.find((t) => t.id === recu.typeOperation)?.label ?? '—'],
            ['Motif', recu.libelle],
            ['Décaissement', `${recu.compteNom} — ${recu.mode}`],
            ['Saisi par (coursier)', recu.saisiPar],
            ['Validé par', user?.name ?? '—'],
            ['Référence interne', recu.id],
          ]}
          totaux={[['Montant décaissé', `${fmt(recu.montant, 3)} TND`]]}
          mentions="Reçu de caisse — à signer par le bénéficiaire. Montants en dinars tunisiens (TND)."
          signatures={['Signature & cachet — Huilerie', 'Signature — Bénéficiaire']}
          onClose={() => setRecu(null)}
        />
      )}
    </div>
  );
}
